// src/modulos/Cadastros/SupplierListReportPage.js

import React, { useState, useMemo, useEffect } from 'react';
import { auth, db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    orderBy,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp
} from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import {
    FormModal,
    ConfirmModal,
    PageHeader,
    Toast,
    Pagination
} from '../../components/common';
import styles from '../Registers/Registers.module.css';
import { countryOptions } from '../../data/countries';

// Collections
const SUPPLIERS_COLLECTION = 'suppliers';
const REPORT_HISTORY_COLLECTION = 'supplier-report-history';
const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};
const CATEGORY_COLLECTION = 'parameters/data/product-categories';
const ACCOUNTING_ACCOUNTS_COLLECTION = 'parameters/data/contas-contabeis';

// Filter options
const REGIME_FISCAL_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'Lucro presumido', label: 'Lucro presumido' },
    { value: 'Lucro Real', label: 'Lucro Real' },
    { value: 'Simples', label: 'Simples' },
    { value: 'Super simples', label: 'Super simples' },
    { value: 'Pessoa física', label: 'Pessoa física' },
    { value: 'Micro Empreendedor Individual', label: 'Micro Empreendedor Individual' },
];

const TIPO_ENTREGA_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'Na transportadora', label: 'Na transportadora' },
    { value: 'À retirar', label: 'À retirar' },
    { value: 'Nosso endereço', label: 'Nosso endereço' },
    { value: 'Triangular', label: 'Triangular' },
];

const TIPO_ATIVIDADE_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'Industrial ou Equiparado a Industrial', label: 'Industrial ou Equiparado a Industrial' },
    { value: 'Outros', label: 'Outros' },
];

const ITEMS_PER_PAGE = 10;

const SupplierListReportPage = () => {
    // Report state
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        searchTerm: '',
        regimeFiscal: '',
        tipoEntrega: '',
        tipoAtividade: '',
        productTypeId: '',
        categoryId: '',
        pais: '',
        dateFrom: '',
        dateTo: ''
    });

    // Pagination for main list
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch suppliers
    const { data: suppliers, loading: suppliersLoading } = useFirestore(SUPPLIERS_COLLECTION, 'codigo');

    // Flag to control when to load parameter collections (only when modal opens)
    const [shouldLoadParams, setShouldLoadParams] = useState(false);

    // Load parameters when modal opens
    const { data: productTypes, loading: loadingTypes } = useSubCollection(
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.parentCollection : null,
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.parentId : null,
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.subCollectionName : null,
        'name'
    );

    const { data: categories, loading: loadingCategories } = useFirestore(
        shouldLoadParams ? CATEGORY_COLLECTION : null,
        'order'
    );

    const { data: accountingAccounts, loading: loadingAccounts } = useFirestore(
        shouldLoadParams ? ACCOUNTING_ACCOUNTS_COLLECTION : null,
        'codigo'
    );

    // Load raw materials for product type/category filtering
    const { data: allRawMaterials, loading: loadingRawMaterials } = useFirestore(
        shouldLoadParams ? 'raw-materials' : null,
        'code'
    );

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Load report history
    useEffect(() => {
        loadReports();
    }, []);

    // Load parameters when modal opens
    useEffect(() => {
        if (isModalOpen && !shouldLoadParams) {
            setShouldLoadParams(true);
        }
    }, [isModalOpen, shouldLoadParams]);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoadingReports(true);
        try {
            const q = query(
                collection(db, REPORT_HISTORY_COLLECTION),
                orderBy('generatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Error loading report history:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoadingReports(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Error deleting report:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // Helper function to get primary address, phone, email
    const getPrimaryAddress = (addresses) => {
        if (!addresses || addresses.length === 0) return '-';
        const primary = addresses[0];
        const parts = [];
        if (primary.endereco) parts.push(primary.endereco);
        if (primary.numero) parts.push(primary.numero);
        if (primary.bairro) parts.push(primary.bairro);
        if (primary.cidade) parts.push(primary.cidade);
        if (primary.uf) parts.push(primary.uf);
        if (primary.cep) parts.push(`CEP: ${primary.cep}`);
        return parts.join(', ') || '-';
    };

    const getAllPhones = (addresses) => {
        if (!addresses || addresses.length === 0) return '-';
        const phones = [];
        addresses.forEach(addr => {
            if (addr.telefone) phones.push(addr.telefone);
            if (addr.celular) phones.push(addr.celular);
            if (addr.contacts && addr.contacts.length > 0) {
                addr.contacts.forEach(contact => {
                    if (contact.telefone) phones.push(contact.telefone);
                    if (contact.celular) phones.push(contact.celular);
                });
            }
        });
        return phones.length > 0 ? [...new Set(phones)].join('; ') : '-';
    };

    const getAllEmails = (addresses) => {
        if (!addresses || addresses.length === 0) return '-';
        const emails = [];
        addresses.forEach(addr => {
            if (addr.email) emails.push(addr.email);
            if (addr.contacts && addr.contacts.length > 0) {
                addr.contacts.forEach(contact => {
                    if (contact.email) emails.push(contact.email);
                });
            }
        });
        return emails.length > 0 ? [...new Set(emails)].join('; ') : '-';
    };

    const getAccountingAccountName = (accountId) => {
        if (!accountId) return '-';
        const account = accountingAccounts?.find(a => a.id === accountId);
        return account?.nome || account?.codigo || accountId;
    };

    // Apply filters to suppliers
    const filteredSuppliers = useMemo(() => {
        if (!suppliers) return [];

        let filtered = [...suppliers];

        // Search term
        if (filters.searchTerm) {
            const term = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(supplier =>
                (supplier.codigo || '').toLowerCase().includes(term) ||
                (supplier.razaoSocial || '').toLowerCase().includes(term) ||
                (supplier.nomeFantasia || '').toLowerCase().includes(term) ||
                (supplier.cnpjCpf || '').toLowerCase().includes(term)
            );
        }

        // Filter by regime fiscal
        if (filters.regimeFiscal) {
            filtered = filtered.filter(supplier => supplier.regimeFiscal === filters.regimeFiscal);
        }

        // Filter by tipo entrega
        if (filters.tipoEntrega) {
            filtered = filtered.filter(supplier => supplier.tipoEntrega === filters.tipoEntrega);
        }

        // Filter by tipo atividade
        if (filters.tipoAtividade) {
            filtered = filtered.filter(supplier => supplier.tipoAtividade === filters.tipoAtividade);
        }

        // Filter by product type (via raw materials)
        if (filters.productTypeId && allRawMaterials) {
            filtered = filtered.filter(supplier => {
                return allRawMaterials.some(material => {
                    const isSupplier = material.suppliers && Array.isArray(material.suppliers) &&
                        material.suppliers.some(s => s.supplierId === supplier.id);
                    return isSupplier && material.productTypeIds?.includes(filters.productTypeId);
                });
            });
        }

        // Filter by category (via raw materials)
        if (filters.categoryId && allRawMaterials) {
            filtered = filtered.filter(supplier => {
                return allRawMaterials.some(material => {
                    const isSupplier = material.suppliers && Array.isArray(material.suppliers) &&
                        material.suppliers.some(s => s.supplierId === supplier.id);
                    return isSupplier && material.categoryIds?.includes(filters.categoryId);
                });
            });
        }

        // Filter by país
        if (filters.pais) {
            filtered = filtered.filter(supplier => supplier.pais === filters.pais);
        }

        // Filter by creation date range
        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(supplier => {
                const createdAt = supplier.createdAt ? new Date(supplier.createdAt) : null;
                return createdAt && createdAt >= fromDate;
            });
        }

        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(supplier => {
                const createdAt = supplier.createdAt ? new Date(supplier.createdAt) : null;
                return createdAt && createdAt <= toDate;
            });
        }

        // Sort by código
        filtered.sort((a, b) => (a.codigo || '').localeCompare(b.codigo || ''));

        return filtered;
    }, [suppliers, filters, allRawMaterials]);

    // Pagination for main list
    const totalPages = Math.ceil((filteredSuppliers?.length || 0) / ITEMS_PER_PAGE);
    const currentSuppliers = (filteredSuppliers || []).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    // Helper functions for display
    const getRegimeFiscalLabel = (value) => {
        return REGIME_FISCAL_OPTIONS.find(r => r.value === value)?.label || value || '-';
    };

    const getTipoEntregaLabel = (value) => {
        return TIPO_ENTREGA_OPTIONS.find(t => t.value === value)?.label || value || '-';
    };

    const getTipoAtividadeLabel = (value) => {
        return TIPO_ATIVIDADE_OPTIONS.find(t => t.value === value)?.label || value || '-';
    };

    const generateExcelReport = async (items, filtersObj, reportTitle, fileName) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Fornecedores');

        // Title row
        worksheet.mergeCells('A1:H1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = reportTitle;
        titleCell.font = { size: 18, bold: true, color: { argb: 'FF2C3E50' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 35;

        // Generation info
        worksheet.mergeCells('A2:H2');
        worksheet.getCell('A2').value = `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
        worksheet.getCell('A2').font = { size: 10, italic: true };
        worksheet.getCell('A2').alignment = { horizontal: 'center' };
        worksheet.getRow(2).height = 22;

        worksheet.mergeCells('A3:H3');
        worksheet.getCell('A3').value = `Gerado por: ${currentUser?.email || 'Usuário'}`;
        worksheet.getCell('A3').font = { size: 10, italic: true };
        worksheet.getCell('A3').alignment = { horizontal: 'center' };
        worksheet.getRow(3).height = 22;

        // Headers
        const headers = [
            'Código', 'Razão Social', 'Inscrição Estadual', 'CNPJ',
            'Endereço', 'Telefone', 'Email', 'Conta Contábil'
        ];

        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2C3E50' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
        worksheet.getRow(4).height = 40;

        // Set column widths
        worksheet.getColumn(1).width = 12;
        worksheet.getColumn(2).width = 35;
        worksheet.getColumn(3).width = 18;
        worksheet.getColumn(4).width = 20;
        worksheet.getColumn(5).width = 45;
        worksheet.getColumn(6).width = 20;
        worksheet.getColumn(7).width = 30;
        worksheet.getColumn(8).width = 25;

        // Data rows
        items.forEach((supplier) => {
            const row = worksheet.addRow([
                supplier.codigo || '-',
                supplier.razaoSocial || '-',
                supplier.ie || '-',
                supplier.cnpjCpf || '-',
                getPrimaryAddress(supplier.addresses),
                getAllPhones(supplier.addresses),
                getAllEmails(supplier.addresses),
                getAccountingAccountName(supplier.contaContabilId)
            ]);

            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle', wrapText: true };
                if (colNumber !== 2 && colNumber !== 5 && colNumber !== 7) {
                    cell.alignment = { ...cell.alignment, horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            row.height = 35;
        });

        // Summary row
        const summaryRow = worksheet.addRow([`Total de fornecedores: ${items.length}`, '', '', '', '', '', '', '']);
        summaryRow.getCell(1).font = { bold: true };
        worksheet.mergeCells(`A${worksheet.rowCount}:H${worksheet.rowCount}`);
        worksheet.getRow(worksheet.rowCount).height = 28;

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (filteredSuppliers.length === 0) {
            addToast('Nenhum fornecedor encontrado com os filtros selecionados.', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            const filterDisplay = {};

            if (filters.regimeFiscal) {
                filterDisplay.regimeFiscalLabel = getRegimeFiscalLabel(filters.regimeFiscal);
            }
            if (filters.tipoEntrega) {
                filterDisplay.tipoEntregaLabel = getTipoEntregaLabel(filters.tipoEntrega);
            }
            if (filters.tipoAtividade) {
                filterDisplay.tipoAtividadeLabel = getTipoAtividadeLabel(filters.tipoAtividade);
            }
            if (filters.productTypeId && productTypes) {
                const type = productTypes.find(t => t.id === filters.productTypeId);
                if (type) filterDisplay.productTypeName = type.name;
            }
            if (filters.categoryId && categories) {
                const cat = categories.find(c => c.id === filters.categoryId);
                if (cat) filterDisplay.categoryName = cat.name;
            }
            if (filters.pais) {
                filterDisplay.pais = filters.pais;
            }
            if (filters.dateFrom) {
                filterDisplay.dateFrom = new Date(filters.dateFrom).toLocaleDateString('pt-BR');
            }
            if (filters.dateTo) {
                filterDisplay.dateTo = new Date(filters.dateTo).toLocaleDateString('pt-BR');
            }
            if (filters.searchTerm) {
                filterDisplay.searchTerm = filters.searchTerm;
            }

            let reportTitle = 'LISTAGEM DE FORNECEDORES';
            if (filterDisplay.productTypeName) reportTitle += ` - ${filterDisplay.productTypeName}`;
            if (filterDisplay.categoryName) reportTitle += ` - ${filterDisplay.categoryName}`;

            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filterParts = [];
            if (filterDisplay.productTypeName) filterParts.push(filterDisplay.productTypeName.replace(/\s/g, '_'));
            if (filterDisplay.categoryName) filterParts.push(filterDisplay.categoryName.replace(/\s/g, '_'));
            if (filterDisplay.regimeFiscalLabel && filterDisplay.regimeFiscalLabel !== 'Todos') filterParts.push(filterDisplay.regimeFiscalLabel.replace(/\s/g, '_'));
            const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
            const fileName = `Listagem_Fornecedores${filterSuffix}_${timestamp}.xlsx`;

            await generateExcelReport(filteredSuppliers, filterDisplay, reportTitle, fileName);

            const reportData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                supplierCount: filteredSuppliers.length,
                filters: {
                    searchTerm: filters.searchTerm || null,
                    regimeFiscal: filters.regimeFiscal,
                    regimeFiscalLabel: filterDisplay.regimeFiscalLabel || null,
                    tipoEntrega: filters.tipoEntrega,
                    tipoEntregaLabel: filterDisplay.tipoEntregaLabel || null,
                    tipoAtividade: filters.tipoAtividade,
                    tipoAtividadeLabel: filterDisplay.tipoAtividadeLabel || null,
                    productTypeId: filters.productTypeId,
                    productTypeName: filterDisplay.productTypeName || null,
                    categoryId: filters.categoryId,
                    categoryName: filterDisplay.categoryName || null,
                    pais: filters.pais || null,
                    dateFrom: filters.dateFrom || null,
                    dateTo: filters.dateTo || null
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), reportData);

            setReports([
                {
                    id: docRef.id,
                    ...reportData,
                    generatedAt: new Date()
                },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Error generating report:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            searchTerm: '',
            regimeFiscal: '',
            tipoEntrega: '',
            tipoAtividade: '',
            productTypeId: '',
            categoryId: '',
            pais: '',
            dateFrom: '',
            dateTo: ''
        });
        setCurrentPage(1);
    };

    const formatDate = (date) => {
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFilterDescription = (filtersObj) => {
        if (!filtersObj) return '-';
        const parts = [];
        if (filtersObj.regimeFiscalLabel && filtersObj.regimeFiscalLabel !== 'Todos') parts.push(`Regime: ${filtersObj.regimeFiscalLabel}`);
        if (filtersObj.tipoEntregaLabel && filtersObj.tipoEntregaLabel !== 'Todos') parts.push(`Entrega: ${filtersObj.tipoEntregaLabel}`);
        if (filtersObj.tipoAtividadeLabel && filtersObj.tipoAtividadeLabel !== 'Todos') parts.push(`Atividade: ${filtersObj.tipoAtividadeLabel}`);
        if (filtersObj.productTypeName) parts.push(`Tipo: ${filtersObj.productTypeName}`);
        if (filtersObj.categoryName) parts.push(`Cat: ${filtersObj.categoryName}`);
        if (filtersObj.pais) parts.push(`País: ${filtersObj.pais}`);
        if (filtersObj.dateFrom) parts.push(`De: ${filtersObj.dateFrom}`);
        if (filtersObj.dateTo) parts.push(`Até: ${filtersObj.dateTo}`);
        if (filtersObj.searchTerm) parts.push(`Busca: ${filtersObj.searchTerm}`);
        if (parts.length === 0) return 'Todos os fornecedores';
        return parts.join(', ');
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        handleResetFilters();
    };

    const isLoading = suppliersLoading;

    // Check if any filter is active
    const hasActiveFilters = filters.regimeFiscal || filters.tipoEntrega || filters.tipoAtividade ||
        filters.productTypeId || filters.categoryId || filters.pais ||
        filters.dateFrom || filters.dateTo || filters.searchTerm;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            {(loadingTypes || loadingCategories || loadingRawMaterials) && (
                <div className={styles.infoBox}>
                    <span>🔄</span>
                    <span>Carregando parâmetros do sistema...</span>
                </div>
            )}

            {/* Search Term */}
            <div className={styles.formGroup}>
                <label>Busca por Código, Razão Social, Fantasia ou Documento</label>
                <input
                    type="text"
                    placeholder="Digite para buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className={styles.input}
                />
            </div>

            {/* Basic Filters Row */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Regime Fiscal</label>
                    <select
                        value={filters.regimeFiscal}
                        onChange={(e) => handleFilterChange('regimeFiscal', e.target.value)}
                        className={styles.select}
                    >
                        {REGIME_FISCAL_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Tipo de Entrega</label>
                    <select
                        value={filters.tipoEntrega}
                        onChange={(e) => handleFilterChange('tipoEntrega', e.target.value)}
                        className={styles.select}
                    >
                        {TIPO_ENTREGA_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Tipo de Atividade</label>
                    <select
                        value={filters.tipoAtividade}
                        onChange={(e) => handleFilterChange('tipoAtividade', e.target.value)}
                        className={styles.select}
                    >
                        {TIPO_ATIVIDADE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Product Type & Category Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Tipo de Produto (Matéria-Prima)</label>
                    <select
                        value={filters.productTypeId}
                        onChange={(e) => handleFilterChange('productTypeId', e.target.value)}
                        className={styles.select}
                        disabled={loadingTypes}
                    >
                        <option value="">Todos os tipos</option>
                        {productTypes?.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                    </select>
                    <small className={styles.fieldHint}>
                        Filtra fornecedores que fornecem matérias-primas deste tipo
                    </small>
                </div>

                <div className={styles.formGroup}>
                    <label>Categoria (Matéria-Prima)</label>
                    <select
                        value={filters.categoryId}
                        onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                        className={styles.select}
                        disabled={loadingCategories}
                    >
                        <option value="">Todas as categorias</option>
                        {categories?.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                    <small className={styles.fieldHint}>
                        Filtra fornecedores que fornecem matérias-primas desta categoria
                    </small>
                </div>

                <div className={styles.formGroup}>
                    <label>País</label>
                    <select
                        value={filters.pais}
                        onChange={(e) => handleFilterChange('pais', e.target.value)}
                        className={styles.select}
                    >
                        <option value="">Todos os países</option>
                        {countryOptions.map(country => (
                            <option key={country.value} value={country.value}>{country.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Date Range Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Início)</label>
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Fim)</label>
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Preview Info */}
            <div className={styles.previewInfo}>
                📊 {filteredSuppliers.length} {filteredSuppliers.length === 1 ? 'fornecedor encontrado' : 'fornecedores encontrados'} com os filtros atuais
            </div>
        </>
    );

    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Gerar Listagem de Fornecedores"
                subtitle="Selecione os filtros desejados para o relatório"
                isLoading={generating}
                submitText={`Gerar Relatório (${filteredSuppliers.length})`}
                cancelText="Cancelar"
                maxWidth="860px"
            >
                {renderFilterForm()}
                <div className={styles.formActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={handleResetFilters} className={styles.cancelButton}>
                        Limpar Filtros
                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Listagem de Fornecedores"
                actionText="Gerar Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading}
            />

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <div className={styles.activeFiltersContainer}>
                    <span className={styles.activeFiltersLabel}>Filtros ativos:</span>
                    {filters.searchTerm && (
                        <span className={styles.filterBadge}>
                            🔍 Busca: {filters.searchTerm}
                        </span>
                    )}
                    {filters.regimeFiscal && (
                        <span className={styles.filterBadge}>
                            📋 Regime: {getRegimeFiscalLabel(filters.regimeFiscal)}
                        </span>
                    )}
                    {filters.tipoEntrega && (
                        <span className={styles.filterBadge}>
                            🚚 Entrega: {getTipoEntregaLabel(filters.tipoEntrega)}
                        </span>
                    )}
                    {filters.tipoAtividade && (
                        <span className={styles.filterBadge}>
                            ⚙️ Atividade: {getTipoAtividadeLabel(filters.tipoAtividade)}
                        </span>
                    )}
                    {filters.productTypeId && productTypes?.find(t => t.id === filters.productTypeId) && (
                        <span className={styles.filterBadge}>
                            🏷️ Tipo: {productTypes.find(t => t.id === filters.productTypeId)?.name}
                        </span>
                    )}
                    {filters.categoryId && categories?.find(c => c.id === filters.categoryId) && (
                        <span className={styles.filterBadge}>
                            📁 Categoria: {categories.find(c => c.id === filters.categoryId)?.name}
                        </span>
                    )}
                    {filters.pais && (
                        <span className={styles.filterBadge}>
                            🌍 País: {filters.pais}
                        </span>
                    )}
                    {filters.dateFrom && (
                        <span className={styles.filterBadge}>
                            📅 De: {new Date(filters.dateFrom).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                    {filters.dateTo && (
                        <span className={styles.filterBadge}>
                            📅 Até: {new Date(filters.dateTo).toLocaleDateString('pt-BR')}
                        </span>
                    )}
                </div>
            )}

            

            {/* Report History Section */}
            <div className={`${styles.tableContainer} ${styles.historyTable}`}>
                <h3 className={styles.tableTitle}>
                    Histórico de Relatórios Gerados
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th>Gerado por</th>
                                <th>Fornecedores</th>
                                <th>Filtros Aplicados</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingReports ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        <div className={styles.spinner} style={{ margin: '0 auto 8px auto' }}></div>
                                        <span>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Gerar Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id} className={styles.tableRow}>
                                        <td>{formatDate(report.generatedAt)}</td>
                                        <td>
                                            <span className={styles.fileName}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.userInfo}>
                                                <span className={styles.userName}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span className={styles.userEmail}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                            {report.supplierCount}
                                        </td>
                                        <td className={styles.filterDescription}>
                                            {getFilterDescription(report.filters)}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteClick(report.id)}
                                                className={`${styles.actionButton} ${styles.deleteButton} ${styles.smallDeleteButton}`}
                                            >
                                                Excluir
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={filteredSuppliers?.length || 0}
                        onPageChange={setCurrentPage}
                        itemsLabel="fornecedores"
                    />
                )}
            </div>
        </div>
    );
};

export default SupplierListReportPage;